import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getApiProxyUrl, getIcmsTransparenteUrl } from '@/core/configs/auth/auth.config';
import {
    redirectToMaintenanceIfNeeded,
    saveSnapshotFromMaintenanceError,
} from './maintenance.service';

export interface CustomResult<T = unknown> {
    success?: boolean;
    Success?: boolean;
    data?: T;
    Data?: T;
    errors?: string[];
    Errors?: string[];
}

const httpErrorStatusCode = [500, 501, 502, 503, 504, 505, 506, 507, 508, 510, 511];

function isBffApiResponse(data: unknown): boolean {
    if (!data || typeof data !== 'object') {
        return false;
    }
    return 'success' in data || 'Success' in data;
}

class HttpCotinService {
    private static httpHeader = {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
    };


    // Público: `assistente-ia.service.ts` precisa montar a URL do endpoint SSE de streaming
    // (`fetch` direto, fora do wrapper axios `Post`/`Get` desta classe).
    static getBaseUrl(): string {
        const baseUrl = getApiProxyUrl();
        const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
        if (normalized.includes('/v1/api/')) {
            return normalized;
        }
        return `${normalized}v1/api/`;
    }

    private static getConfig(extra?: AxiosRequestConfig): AxiosRequestConfig {
        return {
            headers: this.httpHeader,
            withCredentials: true,
            timeout: 60_000,
            ...extra,
        };
    }

    private static extractPayload<T>(body: Record<string, unknown>): T | undefined {
        const direct = body.data ?? body.Data;
        if (direct === undefined || direct === null) {
            return undefined;
        }

        if (typeof direct === 'object' && direct !== null && ('success' in direct || 'Success' in direct)) {
            return this.extractPayload<T>(direct as Record<string, unknown>);
        }

        return direct as T;
    }

    private static unwrap<T>(response: { data: CustomResult<T> | T }): T {
        const raw = response.data;
        if (!raw || typeof raw !== 'object') {
            return raw as T;
        }

        const body = raw as Record<string, unknown>;
        const hasEnvelope = 'success' in body || 'Success' in body;
        if (!hasEnvelope) {
            return raw as T;
        }

        const success = Boolean(body.success ?? body.Success);
        const errors = (body.errors ?? body.Errors) as string[] | undefined;

        if (!success) {
            const message = errors?.join(', ') || 'Erro na requisição.';
            throw new Error(message);
        }

        const payload = this.extractPayload<T>(body);
        return (payload !== undefined ? payload : raw) as T;
    }

    static async Get<T>(uri: string, params: Record<string, unknown> = {}): Promise<T> {
        try {
            const response = await axios.get<CustomResult<T>>(this.getBaseUrl() + uri, {
                ...this.getConfig(),
                params,
            });
            return this.unwrap<T>(response);
        } catch (error) {
            throw this.tratarErro(error, uri);
        }
    }

    static async Post<T>(uri: string, data: unknown = null, signal?: AbortSignal): Promise<T> {
        try {
            const response = await axios.post<CustomResult<T>>(
                this.getBaseUrl() + uri,
                data,
                { ...this.getConfig(), signal }
            );
            return this.unwrap<T>(response);
        } catch (error) {
            throw this.tratarErro(error, uri);
        }
    }

    static async Put<T>(uri: string, data: unknown): Promise<T> {
        try {
            const response = await axios.put<CustomResult<T>>(this.getBaseUrl() + uri, data, this.getConfig());
            return this.unwrap<T>(response);
        } catch (error) {
            throw this.tratarErro(error, uri);
        }
    }

    static async Patch<T>(uri: string, data: unknown): Promise<T> {
        try {
            const response = await axios.patch<CustomResult<T>>(this.getBaseUrl() + uri, data, this.getConfig());
            return this.unwrap<T>(response);
        } catch (error) {
            throw this.tratarErro(error, uri);
        }
    }

    static async Delete<T>(uri: string, params: Record<string, unknown> = {}): Promise<T> {
        try {
            const response = await axios.delete<CustomResult<T>>(this.getBaseUrl() + uri, {
                ...this.getConfig(),
                params,
            });
            return this.unwrap<T>(response);
        } catch (error) {
            throw this.tratarErro(error, uri);
        }
    }

    static async GetBlob(uri: string, params: Record<string, unknown> = {}): Promise<Blob> {
        try {
            const response = await axios.get(this.getBaseUrl() + uri, {
                ...this.getConfig(),
                params,
                responseType: 'blob',
            });
            return response.data as Blob;
        } catch (error) {
            throw this.tratarErro(error, uri);
        }
    }

    private static tratarErro(error: unknown, uri?: string): never {
        // Cancelamento voluntário (AbortController — ex.: botão "Parar" do chat) NÃO é erro de
        // comunicação: relança o erro original intacto para o chamador detectar via
        // `code === 'ERR_CANCELED'`, em vez de virar "Sem resposta do servidor.".
        if (axios.isCancel(error)) {
            throw error;
        }

        if (!axios.isAxiosError(error)) {
            throw error;
        }

        const axiosError = error as AxiosError<CustomResult>;
        const status = axiosError.response?.status;
        const errorBody = axiosError.response?.data;

        if (status === 503) {
            const snapshot = saveSnapshotFromMaintenanceError(axiosError);
            if (snapshot) {
                redirectToMaintenanceIfNeeded(snapshot);
            }
            throw error;
        }

        if (status && httpErrorStatusCode.includes(status)) {
            if (!isBffApiResponse(errorBody)) {
                const hint =
                    typeof window !== 'undefined' && window.location.hostname === 'localhost'
                        ? ' Verifique se o TemplateReact (BFF) está rodando (ex.: https://localhost:7058).'
                        : '';
                throw new Error(
                    `Falha ao comunicar com o servidor (${status}).${hint}`
                );
            }

            const errorInfo = {
                statusCode: status,
                message:
                    errorBody?.errors?.join(', ') ||
                    (errorBody as { message?: string })?.message ||
                    (errorBody as { detail?: string })?.detail ||
                    'Erro desconhecido',
                url: uri || window.location.href,
                timestamp: new Date().toISOString(),
            };
            sessionStorage.setItem('lastHttpError', JSON.stringify(errorInfo));
            window.location.href = '/circuit-breaker-error';
            throw error;
        }

        if (!axiosError.response) {
            const isLocalhost =
                typeof window !== 'undefined' &&
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
            const code = (axiosError as AxiosError & { code?: string }).code;
            const certHint =
                code === 'DEPTH_ZERO_SELF_SIGNED_CERT' ||
                code === 'UNABLE_TO_VERIFY_LEAF_SIGNATURE' ||
                (axiosError.message && /certificate|cert/i.test(axiosError.message))
                    ? ' Certificado autoassinado: o proxy do Vite usa secure:false; confira VITE_API_PROXY=https://localhost:7058/.'
                    : '';
            const hint = isLocalhost
                ? ` Verifique se o BFF TemplateReact está rodando (https://localhost:7058) e se você abriu o app com npm run dev (http://localhost:5173).${certHint}`
                : certHint;
            throw new Error(`Sem resposta do servidor.${hint}`);
        }

        if (status === 401) {
            const errors = errorBody?.errors ?? errorBody?.Errors;
            if (errors?.length) {
                alert(errors.join(', '));
            }
            const isAuthCall =
                typeof uri === 'string' &&
                /Autenticacao\/(Login|LoginLocal|ConsultarLocal|UserSession|Logout)/i.test(uri);
            if (!isAuthCall) {
                setTimeout(() => {
                    window.location.href = getIcmsTransparenteUrl();
                }, 6000);
            }
        }

        if (status === 400) {
            const errors = errorBody?.errors ?? errorBody?.Errors;
            if (errors?.length) {
                throw new Error(errors.join(', '));
            }
        }

        if (status === 404) {
            return null as never;
        }

        const errorEnvelope = errorBody as CustomResult | undefined;
        if (
            errorEnvelope &&
            typeof errorEnvelope === 'object' &&
            ('success' in errorEnvelope || 'Success' in errorEnvelope) &&
            !(errorEnvelope.success ?? errorEnvelope.Success)
        ) {
            const errors = errorEnvelope.errors ?? errorEnvelope.Errors;
            if (errors?.length) {
                throw new Error(errors.join(', '));
            }
            alert('Error interno. Entre em contato com o suporte técnico.');
        }

        const errors = errorEnvelope?.errors ?? errorEnvelope?.Errors;
        throw new Error(errors?.join(', ') || axiosError.message || 'Erro na requisição');
    }

    static prepareQueryString(filtro: Record<string, string | number | null | undefined>): string {
        const filteredParams = Object.entries(filtro).reduce(
            (acc, [key, value]) => {
                if (value !== '' && value != null) {
                    acc[key] = encodeURIComponent(String(value));
                }
                return acc;
            },
            {} as Record<string, string>
        );

        const queryString = new URLSearchParams(filteredParams).toString();
        return queryString ? `?${queryString}` : '';
    }
}

export default HttpCotinService;
